import React, { useEffect, useState } from 'react';
import useSWR from 'swr';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  Textarea,
  UnorderedList,
  ListItem,
  useToast,
} from '@chakra-ui/react';
import baseService from '../../services/base';
import { fetchData } from '../../services/fetch';
import CompanyMultiSelect from '../../components/Catalog/CompanyMultiSelect';

export const MINIMO_DE_EMPRESAS = 3;

const VAZIO = { name: '', description: '', company_ids: [] };

/**
 * GRI-06 — CA02: criação e edição do grupo de comparação.
 *
 * O salvamento tem dois passos quando há empresas vindas de outro grupo: a
 * confirmação da movimentação (CA3.1) e só então o PATCH/POST. A confirmação é
 * UMA, com a lista de quem sai de onde — um diálogo por empresa faria o gestor
 * clicar cinco vezes para montar um grupo de cinco.
 */
export default function ComparisonGroupModal({ isOpen, onClose, group, onSaved }) {
  const toast = useToast();
  const [form, setForm] = useState(VAZIO);
  const [erros, setErros] = useState({});
  const [gravando, setGravando] = useState(false);
  const [movimentacoes, setMovimentacoes] = useState(null);

  const editando = !!group?.id_comparison_group;

  const { data: empresas, isLoading } = useSWR(
    isOpen ? '/comparison-group/companies' : null,
    fetchData
  );

  useEffect(() => {
    if (!isOpen) return;
    setErros({});
    setMovimentacoes(null);
    setForm(
      group
        ? {
            name: group.name || '',
            description: group.description || '',
            company_ids: (group.companies || []).map(
              (empresa) => empresa.id_company
            ),
          }
        : VAZIO
    );
  }, [isOpen, group]);

  // Quem sai de outro grupo nesta seleção. Calculado da MESMA lista que o
  // seletor usa, então o aviso na linha e a confirmação nunca divergem.
  /**
   * Quem sai de outro grupo, perguntado ao SERVIDOR na hora de salvar.
   *
   * A lista carregada quando o modal abriu serve para avisar na linha do
   * seletor, mas envelhece: entre abrir e salvar, outro admin pode ter alocado
   * a mesma empresa. Confirmar movimentação com base numa foto antiga
   * mostraria a lista errada — ou nenhuma — justamente no momento em que a
   * empresa muda de dono.
   */
  const consultarMovimentacoes = async () => {
    const { data } = await baseService.post('/comparison-group/pending-moves', {
      company_ids: form.company_ids,
      id_comparison_group: group?.id_comparison_group ?? null,
    });
    return data || [];
  };

  const validar = () => {
    const encontrados = {};
    if (!form.name.trim()) encontrados.name = 'O nome do grupo é obrigatório';
    if (form.company_ids.length < MINIMO_DE_EMPRESAS) {
      encontrados.company_ids = `Selecione ao menos ${MINIMO_DE_EMPRESAS} empresas para preservar o anonimato`;
    }
    setErros(encontrados);
    return Object.keys(encontrados).length === 0;
  };

  const gravar = async () => {
    setGravando(true);
    try {
      const corpo = {
        name: form.name.trim(),
        description: form.description.trim() || null,
        company_ids: form.company_ids,
      };

      if (editando) {
        await baseService.patch(
          `/comparison-group/${group.id_comparison_group}`,
          corpo
        );
      } else {
        await baseService.post('/comparison-group', corpo);
      }

      toast({
        title: editando ? 'Grupo atualizado' : 'Grupo criado',
        status: 'success',
      });
      onSaved?.();
      onClose();
    } catch (falha) {
      toast({
        title:
          falha?.response?.data?.message ||
          'Não foi possível salvar o grupo de comparação',
        status: 'error',
      });
    } finally {
      setGravando(false);
      setMovimentacoes(null);
    }
  };

  const salvar = async () => {
    if (!validar()) return;

    setGravando(true);
    let aMover = [];
    try {
      aMover = await consultarMovimentacoes();
    } catch {
      // A consulta é uma cortesia, não um portão: se ela falhar, o salvamento
      // segue e o backend faz a movimentação do mesmo jeito. Bloquear aqui
      // impediria o gestor de trabalhar por causa do passo que só serve para
      // avisá-lo.
      aMover = [];
    } finally {
      setGravando(false);
    }

    // A confirmação só aparece quando há de fato empresa mudando de grupo.
    if (aMover.length > 0) {
      setMovimentacoes(aMover);
      return;
    }

    gravar();
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size='lg' scrollBehavior='inside'>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {editando ? 'Editar grupo de comparação' : 'Novo grupo de comparação'}
          </ModalHeader>
          <ModalCloseButton />

          <ModalBody>
            <Stack spacing='16px'>
              <FormControl isInvalid={!!erros.name} isRequired>
                <FormLabel>Nome do Grupo</FormLabel>
                <Input
                  value={form.name}
                  placeholder='Ex.: Varejo e Serviços'
                  onChange={(evento) =>
                    setForm({ ...form, name: evento.target.value })
                  }
                />
                <FormErrorMessage>{erros.name}</FormErrorMessage>
              </FormControl>

              <FormControl>
                <FormLabel>Descrição</FormLabel>
                <Textarea
                  value={form.description}
                  rows={3}
                  onChange={(evento) =>
                    setForm({ ...form, description: evento.target.value })
                  }
                />
              </FormControl>

              <FormControl isInvalid={!!erros.company_ids} isRequired>
                <FormLabel>Empresas</FormLabel>
                <CompanyMultiSelect
                  companies={empresas || []}
                  value={form.company_ids}
                  currentGroupId={group?.id_comparison_group ?? null}
                  isLoading={isLoading}
                  onChange={(ids) => setForm({ ...form, company_ids: ids })}
                />
                <FormHelperText>
                  Mínimo de {MINIMO_DE_EMPRESAS} empresas — com menos, cada uma
                  conseguiria deduzir os números das outras.
                </FormHelperText>
                <FormErrorMessage>{erros.company_ids}</FormErrorMessage>
              </FormControl>
            </Stack>
          </ModalBody>

          <ModalFooter gap='12px'>
            <Button variant='ghost' onClick={onClose}>
              Cancelar
            </Button>
            <Button colorScheme='blue' onClick={salvar} isLoading={gravando}>
              Salvar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* CA3.1 — uma confirmação, com a lista de quem sai de onde. */}
      <Modal
        isOpen={!!movimentacoes}
        onClose={() => setMovimentacoes(null)}
        size='md'
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Mover empresas de outro grupo?</ModalHeader>
          <ModalCloseButton />

          <ModalBody>
            <Alert status='warning' mb='12px' borderRadius='8px'>
              <AlertIcon />
              <Text fontSize='sm'>
                {movimentacoes?.length === 1
                  ? '1 empresa já pertence a outro grupo e será movida.'
                  : `${movimentacoes?.length} empresas já pertencem a outros grupos e serão movidas.`}
              </Text>
            </Alert>

            <UnorderedList spacing='4px'>
              {(movimentacoes || []).map((empresa) => (
                <ListItem key={empresa.id_company} fontSize='sm'>
                  <Text as='span' fontWeight={600}>
                    {empresa.name}
                  </Text>{' '}
                  — sai de {empresa.comparison_group_name}
                </ListItem>
              ))}
            </UnorderedList>

            {/* A consequência que o gestor não vê na tela: o grupo de origem
                pode ficar abaixo do mínimo e perder o benchmarking. */}
            <Box mt='12px'>
              <Text fontSize='xs' color='gray.600'>
                Grupos que ficarem com menos de {MINIMO_DE_EMPRESAS} empresas
                aparecem sinalizados na listagem e ficam sem comparação de
                mercado até serem completados.
              </Text>
            </Box>
          </ModalBody>

          <ModalFooter gap='12px'>
            <Button variant='ghost' onClick={() => setMovimentacoes(null)}>
              Cancelar
            </Button>
            <Button colorScheme='blue' onClick={gravar} isLoading={gravando}>
              Mover e salvar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
